<template lang="pug">
  div
    //- プレミアム決済に問題があった場合
    template(v-if="premium.is_joining && user.has_waiting_auto_recurring_premium")
      a.c-btn.c-btn--lg.c-btn--fill.c-btn--alert.c-btn--inverse(href="/orders/premium?payment_edit=true" target="_blank") Update Payment

    //- 配送不可アイテムが含まれている場合
    template(v-else-if="hasUndeliverableItemInShipments")
      button.c-btn.c-btn--lg.c-btn--fill.c-btn--checkout(disabled) Include undeliverable items

    //- Manga Plusで配送方法がない場合
    template(v-else-if="hasNoShippingMethodForMangaPlus")
      button.c-btn.c-btn--lg.c-btn--fill.c-btn--checkout(disabled) No shipping method available

    //- 決済方法が選択されていない場合
    template(v-else-if="!currentPayment")
      router-link.c-btn.c-btn--lg.c-btn--fill.disabled.c-btn--inverse(:to="'/checkout/payments'") Input new payment method

    template(v-else)
      //- stripe
      template(v-if="Payment.isStripe(currentPayment)", v-cloak)
        button.c-btn.c-btn--lg.c-btn--fill.c-btn--checkout(
        data-track-category="ec_cart",
        data-track-action="instant_checkout",
        data-track-label="click",
        @click="onPlaceYourOrder") Place Your Order

      //- paypal
      template(v-else-if="Payment.isPayPal(currentPayment)", v-cloak)
        template(v-if="premium.is_joining || cart.grand_total === 0")
          button.c-btn.c-btn--lg.c-btn--fill.c-btn--checkout(
          data-track-category="ec_cart",
          data-track-action="pay_with_paypal",
          data-track-label="click",
          @click="onPlaceYourOrder")
            img.u-mrxs(src='/images/cart/icon_paypal.png', alt="PayPal")
            | Pay with PayPal
        template(v-else)
          paypal-buttons(:create-order="onPlaceYourOrder", :return-url='paypalReturnUrl', :cancel-url='paypalCancelUrl')

      //- Manual Order
      template(v-else-if="Payment.isBypass(currentPayment) && !!switchUser", v-cloak)
        button.c-btn.c-btn--lg.c-btn--fill.c-btn--checkout.c-btn--manual-checkout(@click="checkout") Manual Order

    .small.u-mtxs(v-if="!hideByPlacingYourOrder").
      By placing your order, you agree to our #[a(href="/tos", target="_blank") terms of use] and #[a(href="/privacy_guide_checkout", target="_blank") privacy policy].

</template>

<script>
import { Payment } from '../../stores/schema/Payment';
import Shipments from '../../stores/schema/injected/Shipments';

import checkoutJoinConfirmModalComponent from './joinConfirmModal.vue';
import checkoutAlreadyLinkedWarningModalComponent from './alreadyLinkedWaringModal.vue';
import paypalButtonsComponent from './paypalButtons.vue';

export default {
  props: {
    switchUser: {
      type: String
    },
    amazonWidgetUniqueId: {
      type: String
    },
    hideByPlacingYourOrder: {
      type: Boolean
    }
  },
  data() {
    return {
      paypalReturnUrl: null,
      paypalCancelUrl: null
    };
  },
  components: {
    'paypal-buttons': paypalButtonsComponent
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    cart() {
      return this.$store.getters['cart/current']({ populate: ['shipments'] });
    },
    wishlistId() {
      return this.cart.wishlistId;
    },
    premium() {
      return this.cart.premium;
    },
    premiumPlan() {
      return this.$store.getters['premiumPlans/findOne']({
        _id: this.premium.plan
      });
    },
    currentPayment() {
      return this.$store.getters['payments/current'];
    },
    // プレミアム初回入会
    isPremiumMemberJoiningFirstTime() {
      return (
        this.premium.is_joining && !this.user.had_registered_premium_in_past
      );
    },
    // 特典が受け取れるかの判定
    canGetBonus() {
      return (
        this.premium.can_get_bonus &&
        !this.currentPayment.already_used_for_premium
      );
    },
    // 指定された配送先住所には配送できないSKUが含まれているか
    hasUndeliverableItemInShipments() {
      return this.Shipments.hasUndeliverableItemInShipments(
        this.cart.shipments
      );
    },
    // Manga Plusのshipmentで配送方法がないものがあるかどうか
    hasNoShippingMethodForMangaPlus() {
      return this.Shipments.hasNoShippingMethodForMangaPlus(
        this.cart.shipments
      );
    },
    // 決済に利用できない決済方法が選択されているかどうか
    hasUnavailablePaymentMethod() {
      return this.premium.is_joining && this.currentPayment.method === 'amazon';
    },
    Shipments() {
      return Shipments;
    },
    Payment() {
      return Payment;
    }
  },
  methods: {
    checkout() {
      this.$emit('start-loading');

      this.$store
        .dispatch('cart/checkout', {
          wishlistId: this.wishlistId
        })
        .then((res) => {
          if (res.status === 'ok') {
            window.location.href = res.redirect;
          } else {
            if (res.errors.general) {
              this.$alert(
                res.errors.general ||
                  'An error occurred. Please wait a moment before trying again.'
              );
            }
          }
        })
        .catch((err) => {
          if (err.message) {
            this.$alert(
              err.message ||
                'An error occurred. Please wait a moment before trying again.'
            );
          }
          this.$emit('stop-loading');
        });
    },
    checkoutPremium() {
      this.$emit('start-loading');

      const args = {
        wl: this.wishlistId,
        channel_id: this.user.channel_id,
        plan_slug: this.premiumPlan.unique_slug
      };
      if (
        this.user.can_buy_with_annual_plan &&
        this.user.current_subscription_id
      ) {
        args.currentSubscriptionId = this.user.current_subscription_id;
        args.buyWithAnnualPlan = true;
      }

      return this.$store
        .dispatch('premiumPlans/fund', args)
        .then((res) => {
          // Annual plan にスイッチしたときGAイベントを発行
          if (args.buyWithAnnualPlan) {
            window.tom.helper.ga.push([
              'event',
              'Premium',
              'Registered',
              'annual-upgrade'
            ]);
          }
          if (/\/checkout/.test(res.data.redirect_url)) {
            // Stripe の場合、カートの決済処理に進む
            this.checkout();
          } else {
            // PayPal の場合、PayPal の支払い確認画面へリダイレクトさせる
            window.location.href = res.data.redirect_url;
          }
        })
        .catch((err) => {
          if (err.general) {
            this.$alert(
              err.general ||
                'An error occurred. Please wait a moment before trying again.'
            );
          }

          this.$emit('stop-loading');
        });
    },
    checkoutPaypal() {
      this.$emit('start-loading');

      return this.$store
        .dispatch('cart/checkoutWithPaypal', {
          wishlistId: this.wishlistId
        })
        .then((res) => {
          if (res.status === 'ok' && res.redirect === '/cart/complete') {
            window.location.href = res.redirect;
            return;
          } else if (res.status === 'ok' && !res.error) {
            this.paypalReturnUrl = res.return_url;
            this.paypalCancelUrl = res.cancel_url;
            const token = res.redirect.match(/EC-\w+/)[0];
            return token;
          } else {
            if (res.errors && res.errors.general) {
              this.$alert(
                res.errors.general ||
                  'An error occurred. Please wait a moment before trying again.'
              );
            }
          }
        })
        .catch((err) => {
          if (err.message) {
            this.$alert(
              err.message ||
                'An error occurred. Please wait a moment before trying again.'
            );
          }

          this.$emit('stop-loading');
        });
    },
    onPlaceYourOrder() {
      if (
        this.Payment.isPayPal(this.currentPayment) &&
        !this.premium.is_joining
      ) {
        tom.helper.trackAction('ec_cart', 'click', 'pay_with_paypal', 1);
      }

      // プレミアム初回入会 && 特典が受け取れない
      if (this.isPremiumMemberJoiningFirstTime && !this.canGetBonus) {
        return this.$modal.show(
          checkoutAlreadyLinkedWarningModalComponent,
          {
            checkoutPremium: this.checkoutPremium
          },
          {
            width: '90%',
            height: 'auto',
            maxWidth: 560,
            adaptive: true,
            scrollable: true,
            clickToClose: false
          }
        );
      }

      // プレミアム初回入会
      if (
        this.isPremiumMemberJoiningFirstTime &&
        !this.user.is_premium_member
      ) {
        return this.$modal.show(
          checkoutJoinConfirmModalComponent,
          {
            checkoutPremium: this.checkoutPremium
          },
          {
            width: '100%',
            height: 'auto',
            scrollable: true
          }
        );
      }

      // プレミアムチェックボックスがON
      if (this.premium.is_joining) {
        return this.checkoutPremium();
      }

      // stripe
      if (this.Payment.isStripe(this.currentPayment)) {
        return this.checkout();
      }

      // paypal
      if (this.Payment.isPayPal(this.currentPayment)) {
        return this.checkoutPaypal();
      }
    }
  }
};
</script>
